import React, { useCallback, useState } from 'react';
import {
  ApiButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  Typography,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';

import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { UploadedFile } from 'web-library/models/UploadedFile';
import UploadDocuments from '../../pages/accessArrangement/SelectCondition/components/UploadDocuments';
import usePostPresignLinks from './logic/usePostPresignLinks';
import useUploadDocument from './logic/useUploadDocument';
import {
  GroupExamWithdrawalCandidate,
  GroupExamWithdrawalCandidateRequest,
} from '../../api/candidate/exam/model';
import { ApplicationType } from '../../api/candidate/config/model';
import useSetUploadButtonStatus from '../../hooks/useSetUploadButtonStatus';

const useStyles = makeStyles()((theme) =>
({
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(1),
  },
   inputLabel: {
        marginBottom: theme.spacing(1),
        color: theme.color.text.textHeader,
      },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface UploadDocumentsModalProps {
  modalState: boolean;
  candidate: GroupExamWithdrawalCandidate;
  draftFiles: UploadedFile[];
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  onDialogClose: (clearError: boolean) => void;
  mutatePostSearchWithdrawalCandidates: (variables: GroupExamWithdrawalCandidateRequest) => void;
}

const UploadDocumentsModal = ({
  modalState,
  candidate,
  draftFiles,
  setDraftFiles,
  onDialogClose,
  mutatePostSearchWithdrawalCandidates,
}: UploadDocumentsModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [clearError, setClearError] = useState(false);
  const [missingUploadError, setMissingUploadError] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const usePostPresignLinksMutation = usePostPresignLinks({
    files,
    setFiles,
    setShouldSubmit,
    setApiButtonStatus,
    setErrorMsg,
  });

  const { getFilenames } = useUploadDocument({
    files,
    setFiles,
  });

  const handleDialogClose = useCallback(() => {
    mutatePostSearchWithdrawalCandidates({
      uuid: applicationUuid,
      apiPathParam: {
        page: 0,
        pageSize: 10,
      },
    });

    setFiles([]);
    onDialogClose(clearError);
  }, [mutatePostSearchWithdrawalCandidates, applicationUuid, onDialogClose, setFiles, clearError]);

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: handleDialogClose,
  });

  const handleConfirm = () => {
    if (files.length) {
      usePostPresignLinksMutation.mutate({
        payload: {
          applicationType: 'EXAM',
          uuid: candidate.uuid,
          filenames: getFilenames(),
        },
      });
      setClearError(true);
    } else if (candidate.documentRequired && !draftFiles.length) {
      setDisplayErrors(true);
      setMissingUploadError(true);
    } else {
      handleDialogClose();
    }
  };

  return (
    <Dialog open={modalState} maxWidth="md">
      <DialogTitle>{t('withdrawal->>Coordinator.SelectCandidates.UploadDocuments')}</DialogTitle>
      <DialogContent>
        <Typography className={classes.inputLabel} variant="body1">
                 {t('withdrawal->>WithdrawalReason.uploadInstruction')}
              </Typography>
        <UploadDocuments
          files={files}
          setFiles={setFiles}
          hasError={displayErrors}
          applicationType={ApplicationType.GROUP_EXAM}
          draftFiles={draftFiles}
          setDraftFiles={setDraftFiles}
        />
        <ErrorInfo label={errorMsg} />
      </DialogContent>

      <DialogActions>
        <Box className={classes.buttonContainer}>
          {missingUploadError && (
            <Typography className={classes.errString} align="right" variant="body2">
              {t('withdrawal->>Coordinator.SelectCandidates.MissingUploadDocuments')}
            </Typography>
          )}

          <ApiButton
            id="button-confirm-document"
            type="submit"
            status={apiButtonStatus}
            onClick={handleConfirm}
          >
            {t('withdrawal->>Coordinator.SelectCandidates.ConfirmDocuments')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default UploadDocumentsModal;
